import { useState } from "react";
import { toast } from "react-hot-toast";
import { Bell, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "./Switch";
import {
  notificationEvents,
  initialNotificationPreferences,
  type NotificationPreferences,
  type NotificationChannel,
} from "../mockSettings";

export function NotificationsSection() {
  const [preferences, setPreferences] = useState<NotificationPreferences>(initialNotificationPreferences);

  const toggle = (eventId: keyof NotificationPreferences, channel: NotificationChannel) => {
    setPreferences((prev) => ({
      ...prev,
      [eventId]: { ...prev[eventId], [channel]: !prev[eventId][channel] },
    }));
  };

  const handleSave = () => {
    toast.success("Notification preferences saved");
  };

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
      <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Bell className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Notification Preferences &amp; Channels
          </span>
        </div>
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Email &amp; In-app push</span>
      </div>

      <div className="p-3.5 sm:p-4 bg-white/70 dark:bg-slate-900/70 space-y-2">
        <div className="hidden grid-cols-[1fr_auto_auto] items-center gap-6 px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 sm:grid">
          <span>Event Trigger</span>
          <span className="w-12 text-center">Email</span>
          <span className="w-12 text-center">Push</span>
        </div>
        {notificationEvents.map((event) => (
          <div
            key={event.id}
            className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 px-3 py-2.5 shadow-2xs sm:grid-cols-[1fr_auto_auto] sm:gap-6"
          >
            <div className="min-w-0">
              <p className="text-xs font-bold text-slate-800 dark:text-slate-200">{event.label}</p>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium">{event.description}</p>
            </div>
            <div className="flex w-12 items-center justify-center gap-1.5 sm:contents">
              <span className="text-[10px] text-slate-400 sm:hidden font-medium">Email</span>
              <Switch
                checked={preferences[event.id].email}
                onCheckedChange={() => toggle(event.id, "email")}
                aria-label={`Email notifications for ${event.label}`}
              />
            </div>
            <div className="flex w-12 items-center justify-center gap-1.5 sm:contents">
              <span className="text-[10px] text-slate-400 sm:hidden font-medium">Push</span>
              <Switch
                checked={preferences[event.id].push}
                onCheckedChange={() => toggle(event.id, "push")}
                aria-label={`Push notifications for ${event.label}`}
              />
            </div>
          </div>
        ))}

        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
          <Button className="h-9 px-5 text-sm font-semibold rounded-xl shadow-xs gap-1.5" onClick={handleSave}>
            <Check className="h-4 w-4" />
            Save preferences
          </Button>
        </div>
      </div>
    </Card>
  );
}
